/**
 * scCacheService — Two-layer cache (L2) for SC V4 cross-tab validation data.
 *
 * Stores facility details, claims, standards in localStorage with TTL.
 * Zustand is L1 (in-memory, instant). This is L2 (persists across page refreshes).
 *
 * Cache keys are scoped to SC_ID so no data leaks between different SCs.
 * All writes are fire-and-forget (quota errors fail silently — API is always L3 fallback).
 */

const CACHE_VERSION = 'sc_v4';

// TTL constants
const TTL_STANDARDS_MS  = 60 * 60 * 1000; // 60 min — standards change rarely
const TTL_DEFAULT_MS    = 30 * 60 * 1000; // 30 min — facility details / claims

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  cacheVersion: string;
}

// ── Internals ──────────────────────────────────────────────────────────────

function cacheKey(scId: string, suffix: string): string {
  return `${CACHE_VERSION}_${scId}_${suffix}`;
}

function read<T>(k: string, ttlMs: number): T | null {
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);

    // Evict if version mismatch or TTL expired
    if (entry.cacheVersion !== CACHE_VERSION || Date.now() - entry.timestamp > ttlMs) {
      localStorage.removeItem(k);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
}

function write<T>(k: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      cacheVersion: CACHE_VERSION,
    };
    localStorage.setItem(k, JSON.stringify(entry));
  } catch (e) {
    // localStorage quota exceeded — fail silently, API is the L3 fallback
    console.warn('[scCacheService] write failed (quota?):', e);
  }
}

function remove(k: string): void {
  try { 
    localStorage.removeItem(k); 
  } catch { /* no-op */ }
}

// ── Public API ─────────────────────────────────────────────────────────────

export const scCacheService = {
  // SC Standards
  getStandards: (scId: string): string[] | null =>
    read<string[]>(cacheKey(scId, 'standards'), TTL_STANDARDS_MS),
  setStandards: (scId: string, data: string[]): void =>
    write(cacheKey(scId, 'standards'), data),

  // Facility List (summary rows — no process codes)
  getFacilityList: (scId: string): any[] | null =>
    read<any[]>(cacheKey(scId, 'facility_list'), TTL_DEFAULT_MS),
  setFacilityList: (scId: string, data: any[]): void =>
    write(cacheKey(scId, 'facility_list'), data),

  // Facility Detail (per facility — includes standards + process codes)
  getFacilityDetail: (scId: string, facilityId: string): any | null =>
    read<any>(cacheKey(scId, `facility_${facilityId}`), TTL_DEFAULT_MS),
  setFacilityDetail: (scId: string, facilityId: string, data: any): void =>
    write(cacheKey(scId, `facility_${facilityId}`), data),
  removeFacilityDetail: (scId: string, facilityId: string): void =>
    remove(cacheKey(scId, `facility_${facilityId}`)),

  // Product Claims (Tab 7)
  getClaims: (scId: string): string[] | null =>
    read<string[]>(cacheKey(scId, 'claims'), TTL_DEFAULT_MS),
  setClaims: (scId: string, data: string[]): void =>
    write(cacheKey(scId, 'claims'), data),

  /**
   * Nuke ALL cached data for this SC (called on SC_ID change / session end).
   * Uses a prefix scan — O(n) over localStorage keys but called rarely.
   */
  clearAll: (scId: string): void => {
    const prefix = `${CACHE_VERSION}_${scId}_`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  },
};
